import React, { useState } from 'react';
import { ReactComponent as RuniumIcon } from '../assets/runium_icon.svg';
import { openLegalUrl } from '../utils/legalUtils';

const CheckIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
  </svg>
);

const Paywall = ({ onSubscribe, onRedeemPromo, onRestorePurchases, onBack, isLoading }) => {
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);

  const features = [
    'Personalized weekly training plan',
    'Unlimited coaching chat',
    'Adapts when life happens',
  ];

  const handleSubscribe = async () => {
    if (!onSubscribe || isPurchasing) return;
    setIsPurchasing(true);
    setPurchaseError('');
    try {
      const result = await onSubscribe();
      if (result && !result.success) {
        setPurchaseError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setPurchaseError('Something went wrong. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleRedeemPromo = async () => {
    const cleaned = promoCode.trim().replace(/[^A-Z0-9]/gi, '').toUpperCase();
    if (!cleaned) {
      setPromoError('Please enter a promo code');
      return;
    }
    if (cleaned.length > 30) {
      setPromoError('Invalid promo code');
      return;
    }

    setIsRedeeming(true);
    setPromoError('');
    setPromoSuccess('');

    try {
      const result = await onRedeemPromo(cleaned);
      if (result.success) {
        setPromoSuccess(`Code applied! You get ${result.freeDays} days free.`);
        setPromoCode('');
      } else {
        setPromoError(result.error || 'Invalid promo code');
      }
    } catch (err) {
      setPromoError('Failed to redeem code. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleRestore = async () => {
    if (!onRestorePurchases || isRestoring) return;
    setIsRestoring(true);
    try {
      await onRestorePurchases();
    } finally {
      setIsRestoring(false);
    }
  };

  const buttonDisabled = isLoading || isPurchasing;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 overflow-y-auto">
      <div className="w-full max-w-lg mx-auto py-6 md:py-12 px-4">
        {/* Back button */}
        {onBack && (
          <div className="flex justify-start mb-6">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 group"
            >
              <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-blue-500/15 ring-1 ring-blue-500/30 rounded-xl flex items-center justify-center">
              <RuniumIcon className="w-6 h-6 text-white" />
            </div>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-2">
            Unlock Your Potential
          </h1>
          <p className="text-sm text-gray-400 max-w-md mx-auto">
            Get your personalized AI running coach
          </p>
        </div>

        {/* Pricing Card */}
        <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-6 border border-blue-500/50 shadow-2xl shadow-blue-500/10 mb-6">
          {/* Pricing — trial messaging in headline, no badge competing with price */}
          <div className="text-center mb-6">
            <div className="flex items-baseline justify-center">
              <span className="text-4xl font-bold text-white">$3.99</span>
              <span className="text-base text-gray-400 ml-1">/week</span>
            </div>
            <p className="text-sm text-gray-300 mt-2">
              Try free for 7 days, then $3.99/week
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Cancel anytime in Settings
            </p>
          </div>

          {/* Features — three differentiators, not six */}
          <div className="space-y-3 mb-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center">
                <div className="rounded-full p-0.5 mr-3 flex-shrink-0 bg-blue-500">
                  <CheckIcon className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm text-gray-200">{feature}</span>
              </div>
            ))}
          </div>

          {/* Subscribe Button — solid blue with shadow ring (committing to flat) */}
          <button
            onClick={handleSubscribe}
            disabled={buttonDisabled}
            className="w-full py-4 px-4 rounded-xl font-bold text-white bg-blue-500 hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-lg shadow-blue-500/30 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-blue-500"
          >
            {isPurchasing ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing...
              </span>
            ) : 'Start Free Trial'}
          </button>

          {/* Purchase Error */}
          {purchaseError && (
            <p className="text-xs text-red-400 mt-2 text-center">{purchaseError}</p>
          )}

          {/* Restore Purchases */}
          {onRestorePurchases && (
            <button
              onClick={handleRestore}
              disabled={isRestoring}
              className="w-full mt-3 py-2 text-sm text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
            >
              {isRestoring ? 'Restoring...' : 'Restore Purchases'}
            </button>
          )}
        </div>

        {/* Promo Code Section */}
        <div className="bg-gray-800/30 rounded-xl p-4 border border-gray-700/50 mb-6">
          <p className="text-sm font-medium text-gray-300 mb-3">Have a promo code?</p>
          <div className="flex space-x-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => {
                const val = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 30);
                setPromoCode(val);
                setPromoError('');
              }}
              placeholder="Enter code"
              disabled={!!promoSuccess}
              className="flex-1 bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              onClick={handleRedeemPromo}
              disabled={isRedeeming || !promoCode.trim() || !!promoSuccess}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-blue-400 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-600"
            >
              {isRedeeming ? '…' : 'Apply'}
            </button>
          </div>
          {promoError && (
            <p className="text-xs text-red-400 mt-2">{promoError}</p>
          )}
          {promoSuccess && (
            <p className="text-xs text-blue-400 mt-2">{promoSuccess}</p>
          )}
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-4 text-xs text-gray-500 mb-6">
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Secure payment</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
            </svg>
            <span>Cancel anytime</span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>7-day free trial</span>
          </div>
        </div>

        {/* FAQ — collapsed by default to keep the paywall above the fold */}
        <div className="space-y-2 text-left">
          <details className="bg-gray-800/30 rounded-lg group">
            <summary className="cursor-pointer p-3 text-sm font-medium text-white flex items-center justify-between list-none">
              <span>What happens after the trial?</span>
              <svg className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <p className="px-3 pb-3 text-sm text-gray-400">After your 7-day free trial, you'll be charged $3.99/week. Cancel anytime before the trial ends to avoid charges.</p>
          </details>
          <details className="bg-gray-800/30 rounded-lg group">
            <summary className="cursor-pointer p-3 text-sm font-medium text-white flex items-center justify-between list-none">
              <span>How do promo codes work?</span>
              <svg className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <p className="px-3 pb-3 text-sm text-gray-400">Promo codes give you extended free access. After the promo period, your subscription auto-renews at $3.99/week.</p>
          </details>
        </div>
        <div className="text-center space-y-4 mt-4">

          {/* Support */}
          <div className="pt-4 border-t border-gray-700/50">
            <p className="text-sm text-gray-500 mb-2">Need help?</p>
            <a
              href="mailto:support@runium.ai"
              className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors duration-200 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>support@runium.ai</span>
            </a>
          </div>

          {/* Legal Links */}
          <div className="pt-4 flex justify-center items-center gap-4 text-xs">
            <button
              onClick={() => openLegalUrl('https://runium.ai/terms')}
              className="text-gray-500 hover:text-gray-300 underline transition-colors"
            >
              Terms of Service
            </button>
            <span className="text-gray-700">|</span>
            <button
              onClick={() => openLegalUrl('https://runium.ai/privacy')}
              className="text-gray-500 hover:text-gray-300 underline transition-colors"
            >
              Privacy Policy
            </button>
          </div>

          {/* Apple-required subscription disclosure */}
          <div className="pt-3 text-center">
            <p className="text-xs leading-snug text-gray-500 max-w-sm mx-auto">
              A $3.99/week subscription is required after the 7-day free trial.
              Payment will be charged to your Apple ID account at confirmation of purchase.
              Subscription automatically renews unless cancelled at least 24 hours before
              the end of the current period. Your account will be charged for renewal within
              24 hours prior to the end of the current period. You can manage and cancel your
              subscriptions in your App Store account settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paywall;
